import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Pencil, Trash2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { supabase } from '../lib/supabaseClient';
import PublishToLedgerButton from '../components/PublishToLedgerButton';
import DraftingRoom from '../components/DraftingRoom';

export default function Notices() {
  const [items, setItems] = useState([]);
  const [publicIds, setPublicIds] = useState(new Set());
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setErr('');
    try {
      const r = await api.get('/notice/');
      setItems(r.data.notices || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    try {
      const r = await api.get('/ledger/my');
      setPublicIds(new Set((r.data.items || []).map(x => x.id)));
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (id, isSigned) => {
    const { data } = await supabase.auth.getSession();
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notice/${id}/pdf`, {
      headers: { Authorization: `Bearer ${data.session.access_token}` },
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${isSigned ? 'notice' : 'DRAFT-notice'}-${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/notice/${id}`);
      setItems(prev => prev.filter(x => x.id !== id));
      setPublicIds(prev => {
        const s = new Set(prev); s.delete(id); return s;
      });
      setConfirmId(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const closeEditor = async () => {
    setEditingId(null);
    await load();
  };

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="notices-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <Link to="/dashboard" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="back-link">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-12">
        <h1 className="font-headings text-5xl uppercase tracking-tight mb-2">My Notices</h1>
        <p className="font-body text-xs tracking-[0.2em] uppercase text-white/50 mb-10">Every Notice you have generated</p>

        {err && <div className="border border-red-500/40 bg-red-500/10 p-4 font-body text-sm text-red-300 mb-6" data-testid="notices-error">{err}</div>}

        {editingId && (
          <div className="mb-8" data-testid="notices-drafting-wrapper">
            <DraftingRoom noticeId={editingId} onClose={closeEditor} onSigned={closeEditor} />
          </div>
        )}

        {items.length === 0 ? (
          <div className="tactical-card p-12 text-center" data-testid="no-notices">
            <FileText className="h-12 w-12 text-white/20 mx-auto mb-4" strokeWidth={1} />
            <p className="font-body text-sm text-white/60">No notices yet. Generate your first one from the dashboard.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(n => {
              const isSigned = !!n.signed_at;
              const isEditing = editingId === n.id;
              const isPending = ['queued', 'researching'].includes(n.status);
              return (
                <div key={n.id} className="tactical-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`notice-row-${n.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-headings text-lg uppercase">{n.jurisdiction || 'Unknown'}</div>
                      <span
                        className={`inline-block font-body text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border ${isSigned ? 'border-plai-teal/60 text-plai-teal bg-plai-teal/10' : isPending ? 'border-plai-gold/60 text-plai-gold bg-plai-gold/10' : 'border-white/30 text-white/70 bg-black/40'}`}
                        data-testid={`notice-status-${n.id}`}
                      >
                        {isSigned ? 'Signed' : isPending ? 'Generating' : 'Draft'}
                      </span>
                    </div>
                    <div className="font-body text-xs text-white/55 mt-1">
                      To: {n.recipient_name || '(pending)'} | Agency: {n.agency || '-'} | {new Date(n.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <PublishToLedgerButton
                      noticeId={n.id}
                      initialPublic={publicIds.has(n.id)}
                      onChange={(next) => {
                        setPublicIds(prev => {
                          const s = new Set(prev);
                          if (next) s.add(n.id); else s.delete(n.id);
                          return s;
                        });
                      }}
                    />
                    {!isSigned && !isPending && (
                      <button
                        onClick={() => setEditingId(isEditing ? null : n.id)}
                        className="btn-ghost text-xs inline-flex items-center gap-1"
                        data-testid={`edit-${n.id}`}
                      >
                        <Pencil className="h-3 w-3" strokeWidth={1.5} /> {isEditing ? 'Hide' : 'Edit'}
                      </button>
                    )}
                    <button
                      onClick={() => downloadPdf(n.id, isSigned)}
                      className="btn-ghost text-xs inline-flex items-center gap-1"
                      data-testid={`download-${n.id}`}
                    >
                      <Download className="h-3 w-3" strokeWidth={1.5} /> {isSigned ? 'PDF' : 'Draft PDF'}
                    </button>
                    {confirmId === n.id ? (
                      <div className="inline-flex items-center gap-1" data-testid={`confirm-row-${n.id}`}>
                        <span className="font-body text-[10px] uppercase tracking-wider text-red-300 mr-1">Delete forever?</span>
                        <button
                          onClick={() => onDelete(n.id)}
                          disabled={deletingId === n.id}
                          className="px-2 py-1 text-[10px] font-headings uppercase tracking-wider bg-red-500/20 text-red-200 border border-red-500/40 hover:bg-red-500/40 disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`confirm-delete-${n.id}`}
                        >
                          {deletingId === n.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" strokeWidth={1.5} />}
                          Yes, wipe
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-2 py-1 text-[10px] font-headings uppercase tracking-wider border border-white/20 hover:border-white/40 text-white/70"
                          data-testid={`cancel-delete-${n.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(n.id)}
                        className="btn-ghost text-xs text-red-300 hover:text-red-200 inline-flex items-center gap-1"
                        data-testid={`delete-${n.id}`}
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 mt-4" data-testid="no-rollback-warning">
              Note: Deleting a notice does NOT refund your monthly NOI credit. The Odometer never rolls back.
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
